import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import authService from './auth.service';
import {
  SelfRegistrationRequest,
  InviteRegistrationRequest,
  RegistrationResponse,
  RegistrationValidationResponse,
  EmailVerificationRequest,
  ResendVerificationRequest,
  ApiResponse,
  UserInfo,
  ERROR_CODES
} from '@/types/auth.types';

class RegistrationService {
  
  // Validate if registration is allowed (self or invite)
  async validateRegistration(email?: string, invitationToken?: string): Promise<RegistrationValidationResponse['data']> {
    try {
      const params: any = {};
      if (email) params.email = email;
      if (invitationToken) params.invitation_token = invitationToken;
      
      const response = await apiClient.get<RegistrationValidationResponse>(API_ENDPOINTS.REGISTRATION.VALIDATE, { params });
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error('Failed to validate registration');
    } catch (error: any) {
      console.error('Registration validation error:', error);
      throw new Error(
        error.response?.data?.error?.error_description ||
        error.response?.data?.description ||
        'Unable to validate registration'
      );
    }
  }
  
  // Self registration for new users - ALWAYS requires email verification
  async  selfRegister(userData: SelfRegistrationRequest): Promise<{ user: UserInfo; requiresVerification: boolean; message: string }> {
    try {
      // First validate if self-registration is allowed
      const validation = await this.validateRegistration(userData.email);
      if (!validation.allowed || validation.registration_type !== 'self') {
        throw new Error(ERROR_CODES.SELF_REGISTRATION_DISABLED);
      }
      
      const response = await apiClient.post<RegistrationResponse>(API_ENDPOINTS.REGISTRATION.SELF, userData);
      
      if (response.data.success && response.data.data) {
        const { user_info, message, email_verification_required } = response.data.data;
        
        // Self-registration NEVER returns tokens - email verification is always required
        // Do NOT attempt to set up session or store tokens
        
        return {
          user: user_info,
          requiresVerification: email_verification_required, // Should always be true
          message: message
        };
      }
      
      throw new Error(response.data.description || 'Registration failed');
    } catch (error: any) {
      console.error('Self registration error:', error);

      // Re-throw the original error to preserve the response data
      throw error;
    }
  }
  
  // Register with invitation token - ALWAYS returns tokens for immediate login
  async registerWithInvite(inviteData: InviteRegistrationRequest): Promise<{ user: UserInfo; token: string }> {
    try {
      // First validate the invitation token
      const validation = await this.validateRegistration(undefined, inviteData.invitation_token);
      if (!validation.allowed || validation.registration_type !== 'invite') {
        throw new Error(ERROR_CODES.INVALID_INVITATION_TOKEN);
      }
      
      const response = await apiClient.post<RegistrationResponse>(API_ENDPOINTS.REGISTRATION.INVITE, inviteData);
      
      if (response.data.success && response.data.data) {
        const { 
          user_info, 
          access_token, 
          refresh_token,
          expires_in 
        } = response.data.data;
        
        // Invite registration ALWAYS returns tokens - validate this
        if (!access_token) {
          throw new Error('Invalid response: Expected access token for invite registration');
        }
        
        // Set up session for immediate login
        authService.setAccessToken(access_token, expires_in);
        if (refresh_token) {
          authService.setRefreshToken(refresh_token);
        }
        authService.setUser(user_info);
        
        // Set authorization header for subsequent requests
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        return {
          user: user_info,
          token: access_token
        };
      }
      
      throw new Error(response.data.description || 'Registration failed');
    } catch (error: any) {
      console.error('Invite registration error:', error);
      
      // Map error codes to user-friendly messages
      const errorCode = error.response?.data?.error?.error;
      const errorMessage = ERROR_CODES[errorCode as keyof typeof ERROR_CODES] || 
                          error.response?.data?.error?.error_description || 
                          error.response?.data?.description ||
                          error.message ||
                          'Registration failed';
      
      throw new Error(errorMessage);
    }
  }
  
  // Verify email with verification token
  async verifyEmail(verificationToken: string): Promise<{ success: boolean; message: string; redirectUrl?: string }> {
    try {
      const response = await apiClient.post<ApiResponse<{
        success: boolean;
        message: string;
        redirect_url?: string;
      }>>(API_ENDPOINTS.REGISTRATION.VERIFY_EMAIL, {
        verification_token: verificationToken
      } as EmailVerificationRequest);
      
      if (response.data.success && response.data.data) {
        return {
          success: response.data.data.success,
          message: response.data.data.message,
          redirectUrl: response.data.data.redirect_url
        };
      }
      
      throw new Error('Email verification failed');
    } catch (error: any) {
      console.error('Email verification error:', error);
      throw new Error(
        error.response?.data?.error?.error_description ||
        error.response?.data?.description ||
        'Email verification failed'
      );
    }
  }
  
  // Resend verification email
  async resendVerification(email: string): Promise<{ success: boolean; message: string; nextResendAt?: string }> {
    try {
      const response = await apiClient.post<ApiResponse<{
        success: boolean;
        message: string;
        next_resend_available_at?: string;
      }>>(API_ENDPOINTS.REGISTRATION.RESEND_VERIFICATION, {
        email
      } as ResendVerificationRequest);
      
      if (response.data.success && response.data.data) {
        return {
          success: response.data.data.success,
          message: response.data.data.message,
          nextResendAt: response.data.data.next_resend_available_at
        };
      }
      
      throw new Error('Failed to resend verification email');
    } catch (error: any) {
      console.error('Resend verification error:', error);
      
      // Check for rate limiting
      if (error.response?.status === 429) {
        throw new Error('Too many requests. Please wait before trying again.');
      }
      
      throw new Error(
        error.response?.data?.error?.error_description ||
        error.response?.data?.description ||
        'Failed to resend verification email'
      );
    }
  }
  
  // Helper to check if email exists
  async checkEmailAvailability(email: string): Promise<boolean> {
    try {
      const validation = await this.validateRegistration(email);
      // If validation passes without EMAIL_ALREADY_EXISTS error, email is available
      return validation.allowed && validation.registration_type !== 'not_allowed';
    } catch (error: any) {
      // If we get an EMAIL_ALREADY_EXISTS error, email is not available
      if (error.response?.data?.error?.error === 'EMAIL_ALREADY_EXISTS') {
        return false;
      }
      // For other errors, assume email might be available
      return true;
    }
  }
  
  // Helper to get invitation details
  async getInvitationDetails(invitationToken: string): Promise<RegistrationValidationResponse['data']['invitation_info']> {
    try {
      const validation = await this.validateRegistration(undefined, invitationToken);
      
      if (validation.allowed && validation.registration_type === 'invite' && validation.invitation_info) {
        return validation.invitation_info;
      }
      
      throw new Error('Invalid or expired invitation');
    } catch (error: any) {
      console.error('Get invitation details error:', error);
      throw new Error(
        error.response?.data?.error?.error_description ||
        error.response?.data?.description ||
        error.message ||
        'Failed to get invitation details'
      );
    }
  }
  
  // Helper to check if self-registration is enabled
  async isSelfRegistrationEnabled(): Promise<boolean> {
    try {
      const validation = await this.validateRegistration();
      return validation.registration_type === 'self' && validation.allowed;
    } catch (error) {
      // If error, assume self-registration is disabled
      return false;
    }
  }
}

export const registrationService = new RegistrationService();
export default registrationService;